import {
  Accordion,
  Badge,
  BBSpinner,
  Box,
  Button,
  Flex,
  ProgressIndicator,
  Text,
} from '@rocket/ui';
import { useEffect, useRef, useState } from 'react';
import { LuLock, LuTrash2 } from 'react-icons/lu';
import { toast } from 'react-toastify';
import { useAsyncJobPolling } from '../hooks/useAsyncJobPolling';
import { useFtueHost } from '../host';
import type { ChatRuleCreated, PlaybookRule, PlaybookSection } from '../onboarding-api';
import { getPlaybookRules, getPlaybookSections, removeRuleFromPlaybook } from '../onboarding-api';
import { FirstPartyPaperChatBox } from './FirstPartyPaperChatBox';
import { OnboardingChatBox } from './OnboardingChatBox';
import { ReviewChatBox } from './ReviewChatBox';

interface SectionWithRules {
  section: PlaybookSection;
  rules: Array<PlaybookRule>;
}

interface Props {
  playbookId: string;
  playbookName: string;
  paperType: 'first_party' | 'third_party';
  contractType: string;
  skippedRules?: Array<string>;
  jobId?: string;
  onContinue: () => void;
  /** When false, hides rule creation chat boxes */
  enableChatRuleCreation?: boolean;
}

export function OnboardingFinalizePlaybook({
  playbookId,
  playbookName,
  paperType,
  contractType,
  skippedRules = [],
  jobId,
  onContinue,
  enableChatRuleCreation = true,
}: Props) {
  const { bbAxios, selectInDocument } = useFtueHost();

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isPolling, setIsPolling] = useState(false);
  const [sectionsWithRules, setSectionsWithRules] = useState<Array<SectionWithRules>>([]);
  const [removingIds, setRemovingIds] = useState<Set<string>>(new Set());
  const [pendingSkippedRules, setPendingSkippedRules] = useState<Array<string>>(skippedRules);

  const isFirstParty = paperType === 'first_party';

  // Capability-branch seam (see FtueHostAdapter): no-op where no live doc exists.
  const highlightInDocument = async (searchText: string) => {
    if (!selectInDocument) {
      return;
    }
    const query = searchText.trim();
    if (!query) {
      return;
    }
    try {
      await selectInDocument(query);
    } catch {
      toast.error('Unable to navigate to the selected rule text.');
    }
  };

  const handleChatRulesCreated = (created: Array<ChatRuleCreated>) => {
    // Remove resolved rules from the pending list
    const createdNames = new Set(created.map((r) => r.name.toLowerCase()));
    setPendingSkippedRules((prev) => prev.filter((r) => !createdNames.has(r.toLowerCase())));
    // Reload the rule list so newly created rules appear
    void reloadRules();
    toast.success(
      `${created.length} rule${created.length !== 1 ? 's' : ''} configured successfully.`
    );
  };

  const reloadRules = async (isInitial = false) => {
    if (isInitial) {
      setIsInitialLoading(true);
    } else {
      setIsPolling(true);
    }
    try {
      const [sections, rules] = await Promise.all([
        getPlaybookSections(bbAxios, playbookId),
        getPlaybookRules(bbAxios, playbookId),
      ]);

      const sectionMap = new Map(sections.map((s) => [s._id, s]));

      const grouped: Array<SectionWithRules> = sections
        .map((section) => ({
          section,
          rules: rules.filter((r) => r.sections?.includes(section._id)),
        }))
        .filter((g) => g.rules.length > 0);

      const unassigned = rules.filter((r) => !r.sections?.some((sid) => sectionMap.has(sid)));
      if (unassigned.length > 0) {
        grouped.push({
          section: { _id: '__unassigned__', name: 'Other Rules' },
          rules: unassigned,
        });
      }

      setSectionsWithRules(grouped);
    } catch {
      toast.error('Failed to load playbook contents.');
    } finally {
      if (isInitial) {
        setIsInitialLoading(false);
      } else {
        setIsPolling(false);
      }
    }
  };

  const ruleCountRef = useRef(0);

  // Poll enhancement job progress
  const {
    isPolling: isEnhancing,
    percentComplete,
    message: enhanceMessage,
    current: enhanceCurrent,
    total: enhanceTotal,
  } = useAsyncJobPolling(bbAxios, {
    jobId: jobId ?? null,
    onComplete: () => {
      void reloadRules();
    },
  });

  // Keep ref in sync with state
  useEffect(() => {
    ruleCountRef.current = sectionsWithRules.reduce((n, g) => n + g.rules.length, 0);
  }, [sectionsWithRules]);

  // Poll for rules until the count stabilises (save-rules runs in the background)
  useEffect(() => {
    let cancelled = false;
    let stableChecks = 0;
    let prevCount = -1;

    const poll = async () => {
      // Initial load
      await reloadRules(true);
      // Allow a tick for state to update
      await new Promise((r) => setTimeout(r, 100));

      // Poll for updates without showing spinner
      while (!cancelled) {
        await reloadRules(false);
        // Allow a tick for state to update
        await new Promise((r) => setTimeout(r, 100));
        const currentCount = ruleCountRef.current;
        if (currentCount === prevCount && currentCount > 0) {
          stableChecks++;
          if (stableChecks >= 2) {
            break; // count unchanged for 2 consecutive polls
          }
        } else {
          stableChecks = 0;
        }
        prevCount = currentCount;
        await new Promise((r) => setTimeout(r, 3000));
      }
    };

    void poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bbAxios, playbookId]);

  const handleRemove = async (sectionId: string, ruleId: string) => {
    setRemovingIds((prev) => new Set(prev).add(ruleId));
    try {
      await removeRuleFromPlaybook(bbAxios, { playbookId, ruleId });
      setSectionsWithRules((prev) =>
        prev
          .map((g) =>
            g.section._id === sectionId
              ? { ...g, rules: g.rules.filter((r) => r._id !== ruleId) }
              : g
          )
          .filter((g) => g.rules.length > 0)
      );
    } catch {
      toast.error('Failed to remove rule. Please try again.');
    } finally {
      setRemovingIds((prev) => {
        const next = new Set(prev);
        next.delete(ruleId);
        return next;
      });
    }
  };

  const totalRules = sectionsWithRules.reduce((sum, g) => sum + g.rules.length, 0);

  const accordionItems = sectionsWithRules.map(({ section, rules }) => ({
    value: section._id,
    title: `${section.name} (${rules.length})`,
    content: (
      <Flex.V gap="2">
        {rules.map((rule) => (
          <Box
            key={rule._id}
            borderWidth="1px"
            borderRadius="md"
            p="3"
            bg="bg.surface"
            borderColor="border"
          >
            <Flex.H align="flex-start" gap="2">
              <Flex.V gap="1" flex="1">
                <Flex.H align="center" gap="2" wrap="wrap">
                  {selectInDocument ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      px="0"
                      minH="auto"
                      h="auto"
                      onClick={() => {
                        const query =
                          rule.sampleEdit?.before || rule.sampleEdits?.[0]?.before || '';
                        void highlightInDocument(query);
                      }}
                    >
                      <Text fontWeight="600" fontSize="sm" textDecoration="underline">
                        {rule.name}
                      </Text>
                    </Button>
                  ) : (
                    <Text fontWeight="600" fontSize="sm">
                      {rule.name}
                    </Text>
                  )}
                  {Boolean(rule.protectionConfig?.enabled || rule.protection_config?.enabled) && (
                    <Badge size="xs" colorPalette="orange" variant="subtle">
                      <LuLock /> Protected
                    </Badge>
                  )}
                </Flex.H>
                {rule.description && (
                  <Text fontSize="sm" color="fg.muted">
                    {rule.description}
                  </Text>
                )}
              </Flex.V>
              <Button
                size="xs"
                variant="ghost"
                colorPalette="red"
                onClick={() => handleRemove(section._id, rule._id)}
                disabled={removingIds.has(rule._id)}
                loading={removingIds.has(rule._id)}
                aria-label="Remove rule"
              >
                <LuTrash2 size={14} />
              </Button>
            </Flex.H>
          </Box>
        ))}
      </Flex.V>
    ),
  }));

  return (
    <Flex.V gap="5" p="6">
      <Flex.V gap="1">
        <Text fontWeight="700" fontSize="lg">
          Review Your Playbook
        </Text>
        {!isInitialLoading && (
          <Text fontSize="sm" color="fg.muted">
            {totalRules} rule{totalRules !== 1 ? 's' : ''} in {playbookName}
            {isPolling && <span> (updating…)</span>}
          </Text>
        )}
      </Flex.V>

      {isEnhancing && (
        <Flex.V gap="2">
          <ProgressIndicator
            message={enhanceMessage || 'Enhancing rules…'}
            current={enhanceCurrent}
            total={enhanceTotal}
            percentComplete={percentComplete}
          />
        </Flex.V>
      )}

      {isInitialLoading ? (
        <Flex.V align="center" py="8" gap="3">
          <BBSpinner />
          <Text fontSize="sm" color="fg.muted">
            Loading playbook…
          </Text>
        </Flex.V>
      ) : (
        <Accordion
          items={accordionItems}
          multiple
          collapsible
          defaultValue={[sectionsWithRules[0]?.section._id ?? '']}
        />
      )}

      {/* Chat to configure skipped rules */}
      {enableChatRuleCreation && !isFirstParty && pendingSkippedRules.length > 0 && (
        <Box borderWidth="1px" borderRadius="md" p="4" bg="bg.subtle" borderColor="border">
          <OnboardingChatBox
            playbookId={playbookId}
            contractType={contractType}
            skippedRules={pendingSkippedRules}
            onRulesCreated={handleChatRulesCreated}
          />
        </Box>
      )}

      {/* Playbook assistant — add/search/describe rules via chat */}
      {enableChatRuleCreation && !isFirstParty && (
        <ReviewChatBox
          playbookId={playbookId}
          contractType={contractType}
          onRulesCreated={(created) => {
            const createdNames = new Set(created.map((r) => r.name.toLowerCase()));
            setPendingSkippedRules((prev) =>
              prev.filter((r) => !createdNames.has(r.toLowerCase()))
            );
            void reloadRules();
            toast.success(`${created.length} rule${created.length !== 1 ? 's' : ''} added.`);
          }}
        />
      )}

      {enableChatRuleCreation && isFirstParty && (
        <FirstPartyPaperChatBox
          playbookId={playbookId}
          contractType={contractType}
          onRulesCreated={(created) => {
            const createdNames = new Set(created.map((r) => r.name.toLowerCase()));
            setPendingSkippedRules((prev) =>
              prev.filter((r) => !createdNames.has(r.toLowerCase()))
            );
            void reloadRules();
            toast.success(`${created.length} rule${created.length !== 1 ? 's' : ''} added.`);
          }}
        />
      )}

      <Flex.V
        position="sticky"
        bottom="0"
        bg="bg"
        py="3"
        borderTopWidth="1px"
        borderTopColor="border.muted"
        mt="4"
        gap="2"
      >
        {isEnhancing && (
          <Text fontSize="sm" color="fg.muted" textAlign="center">
            Your playbook is ready to use. Feel free to continue, and we'll keep refining your rules
            in the background.
          </Text>
        )}
        <Flex.H justify="flex-end">
          <Button colorPalette="gray" onClick={onContinue}>
            Continue
          </Button>
        </Flex.H>
      </Flex.V>
    </Flex.V>
  );
}
